// /admin/teams — the team writes that change who may do what, behind the house
// dialogs (CRUD-CONFIRM-STANDARD, 2026-10-30).
//
//   • RENAME — a team could be created but never renamed (the SDK's
//     `updateTeam` had no caller). A prompt seeded with the current name; the
//     write is PATCH /organizations/:orgId/teams/:teamId `{ name }`, gated
//     server-side on `admin.teams`.
//   • DELETE — a team could not be deleted at all (`deleteTeam` had no
//     caller). The house confirm names the team and says what goes with it;
//     the write is DELETE /organizations/:orgId/teams/:teamId (`admin.teams`
//     plus the server's can-manage check for a confidential team). A SYSTEM
//     team is never offered: the seeder owns it (server Team.isSystem — "you
//     can't delete this").
//   • THE SELF-GUARD — a permission switch writes at once, and an admin could
//     switch off a permission their own access rests on (`admin.teams`,
//     `admin.team_permissions`, …) with one stray click. A switch turned OFF
//     on the signed-in person's OWN team (they are not an owner: owners bypass
//     every check) asks first; the page decides that and passes `askSelf`.
//     Every other switch stays one click, as before.
//
// Each export is a registered function (`el.call('adminRenameTeam', team)`);
// `this` is the calling element (state root, `sdk`, `call`, `router`). The
// write runs in the dialog's `onConfirm` only. No imports: the page passes
// what it resolved (Rule FA001 — functions read nothing from sibling files).
// The private helpers carry a `_team` prefix: frank lifts module-level
// helpers into ONE emitted globalScope, where a same-named helper of another
// file (`_fail` in pages/admin/teams.js, `_sdkOf` in moduleConsole.js) would
// collide (FA202).

const _teamRootOf = (el) => (el && el.state && el.state.root) || null

const _teamSdkOf = (el) =>
  (el && el.sdk) || (el && el.context && el.context.methods && el.context.methods.sdk) || null

// A failure the person acted on is SAID (the shape of the page's `_fail`).
const _teamFail = (el, key, err) => {
  el.call('openNotification', {
    title: el.call('polyglot', 'common.state.error'),
    message: el.call('polyglot', key).replace('{error}', (err && err.message) || err || ''),
    type: 'error'
  })
}

// The signed-in person, the way the team index knows people: the user id on
// the entry, or the email it is filed under.
export const adminTeamIsMe = (root, email, userId) => {
  const me = String((root && root.currentUserId) || '')
  const myEmail = String((root && root.currentUserEmail) || '').toLowerCase()
  if (me && userId != null && String(userId) === me) return true
  return !!myEmail && String(email || '').toLowerCase() === myEmail
}

// Is the signed-in person a member of `teamId`? Reads the two indexes the
// members card is built from: `root.teamsByEmail` (email → [{ id, role,
// userId }]) and `root.teamsByUserId` (user id → [{ id, … }]).
export const adminTeamHasMe = (root, teamId) => {
  if (!root || !teamId) return false
  const me = String(root.currentUserId || '')
  const byId = (me && root.teamsByUserId && root.teamsByUserId[me]) || []
  if (byId.some((t) => t && t.id === teamId)) return true
  const byEmail = root.teamsByEmail || {}
  for (const email of Object.keys(byEmail)) {
    const entry = (byEmail[email] || []).find((t) => t && t.id === teamId)
    if (entry && adminTeamIsMe(root, email, entry.userId)) return true
  }
  return false
}

// One permission switch. Writes at once — unless it turns a permission OFF
// and `askSelf` says the team is the signed-in person's own (and they are not
// an owner): then the house confirm asks first. The question says what may
// go: the permission, unless their role or another of their teams gives it.
export function adminToggleTeamPermission(team, permKey, { askSelf = false } = {}) {
  const el = this
  const root = _teamRootOf(el)
  if (!root || !team || !permKey) return undefined
  const next = new Set(team.permissions || [])
  const turningOff = next.has(permKey)
  if (turningOff) next.delete(permKey)
  else next.add(permKey)
  const permissions = [...next]
  // The switch paints from `team.permissions` (its `onCheck`), so a refused
  // write — or a cancelled question — repaints it from the record. Nudge the
  // repaint to be safe.
  const repaint = () => root.update({ _permRetry: (root._permRetry || 0) + 1 })
  const write = async () => {
    try {
      await el.call('updateTeamPermissions', root, {
        orgId: team.orgId,
        teamId: team.id,
        permissions
      })
    } catch (err) {
      console.warn('[admin/teams] permission toggle failed', err)
      repaint()
    }
  }
  if (!turningOff || !askSelf) return write()
  el.call('confirmDialog', {
    title: el
      .call('polyglot', 'admin.teams.perm.selfTitle')
      .replace('{permission}', el.call('permLabelText', permKey)),
    message: el.call('polyglot', 'admin.teams.perm.selfMessage'),
    confirmText: el.call('polyglot', 'admin.teams.perm.turnOff'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: write,
    onCancel: repaint
  })
  return undefined
}

// Rename: the house prompt, seeded with the current name. An empty or
// unchanged name writes nothing.
export function adminRenameTeam(team) {
  const el = this
  const root = _teamRootOf(el)
  if (!root || !team) return
  el.call('promptDialog', {
    title: el.call('polyglot', 'admin.teams.rename.title'),
    message: el.call('polyglot', 'admin.teams.create.prompt'),
    placeholder: el.call('polyglot', 'admin.teams.create.prompt'),
    defaultValue: team.name || '',
    confirmText: el.call('polyglot', 'common.action.rename'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    onConfirm: async (value) => {
      const name = String(value == null ? '' : value)
        .replace(/\s+/g, ' ')
        .trim()
      if (!name || name === team.name) return
      const sdk = _teamSdkOf(el)
      try {
        if (!sdk || typeof sdk.updateTeam !== 'function') throw new Error('SDK not available')
        const res = await sdk.updateTeam(team.orgId, team.id, { name })
        const stored = (res && (res.data || res)) || {}
        const saved = typeof stored.name === 'string' && stored.name ? stored.name : name
        root.update({
          teams: (root.teams || []).map((t) => (t && t.id === team.id ? { ...t, name: saved } : t))
        })
      } catch (err) {
        console.warn('[admin/teams] rename failed', err)
        _teamFail(el, 'admin.teams.rename.failed', err)
      }
    }
  })
}

// Drop one team id from an index of `key → [{ id, … }]`.
const _withoutTeam = (index, teamId) => {
  const out = {}
  for (const key of Object.keys(index || {})) {
    out[key] = (index[key] || []).filter((t) => t && t.id !== teamId)
  }
  return out
}

// Delete: the house confirm names the team; the message says what goes with
// it (and says so to the signed-in person when it is their own team). After
// the server deleted it, the team leaves the lists and the page returns to
// the team list.
export function adminDeleteTeam(team) {
  const el = this
  const root = _teamRootOf(el)
  if (!root || !team || team.isSystem) return
  const mine = adminTeamHasMe(root, team.id)
  el.call('confirmDialog', {
    title: el
      .call('polyglot', 'admin.teams.delete.title')
      .replace('{team}', team.name || team.slug || ''),
    message: el.call(
      'polyglot',
      mine ? 'admin.teams.delete.messageSelf' : 'admin.teams.delete.message'
    ),
    confirmText: el.call('polyglot', 'common.action.delete'),
    cancelText: el.call('polyglot', 'common.action.cancel'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: async () => {
      const sdk = _teamSdkOf(el)
      try {
        if (!sdk || typeof sdk.deleteTeam !== 'function') throw new Error('SDK not available')
        await sdk.deleteTeam(team.orgId, team.id)
      } catch (err) {
        console.warn('[admin/teams] delete failed', err)
        _teamFail(el, 'admin.teams.delete.failed', err)
        return
      }
      // The invite drafts belong to the deleted team; the Back button
      // clears them the same way.
      root.update({
        teams: (root.teams || []).filter((t) => t && t.id !== team.id),
        teamsByEmail: _withoutTeam(root.teamsByEmail, team.id),
        teamsByUserId: _withoutTeam(root.teamsByUserId, team.id),
        _teamInviteEmail: '',
        _teamInviteStatus: null
      })
      const routeRoot = el.call('findRouteRoot', el)
      if (routeRoot) el.router('/admin/teams', routeRoot)
    }
  })
}
